import React from 'react';
import { useIntl } from '@leagueplatform/locales';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import {
  Box,
  BoxProps,
  BodyTwo,
  Link,
  genesisStyled,
  VisuallyHidden,
} from '@leagueplatform/genesis-commons';
import { Icon } from '@leagueplatform/genesis-core';
import { useConfigProperty } from '@leagueplatform/core';
import { defaultHandleLink } from '@leagueplatform/web-common';
import { TransactionGroup } from '../types/transactions.types';

const TableCell = (props: BoxProps) => (
  <BodyTwo
    as="td"
    paddingY="one"
    paddingX="quarter"
    verticalAlign="middle"
    borderBottomColor="onSurface.border.subdued"
    borderBottomStyle="solid"
    borderBottomWidth="thin"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
);

const StyledTableCell = genesisStyled(TableCell)`
white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
`;

interface TransactionProps {
  transaction: TransactionGroup;
  lifemarketUrl?: string;
}

export const Transaction = ({
  transaction: {
    date,
    title,
    amount,
    amountType,
    currency,
    invoiceId,
    claimId,
    referenceId,
    paymentId,
    type,
    accountsList,
    isLifemarketTransaction,
  },
  lifemarketUrl,
}: TransactionProps) => {
  const { formatMessage, $tmap, formatDate, formatNumber } = useIntl();
  const navigate = useNavigate();

  const typeMessage = $tmap({
    CLAIM: 'CLAIM',
    LIFEMARKET: 'LIFEMARKET',
    ALLOCATION: 'ALLOCATION',
    TOP_UP: 'TOP_UP',
    REFUND: 'REFUND',
    ADJUSTMENT: 'ADJUSTMENT',
    ROLLED_OVER: 'ROLLED_OVER',
  });

  const accountMessageMap = $tmap({
    LSA: 'LSA',
    HSA: 'HSA',
    CREDIT_CARD: 'CREDIT_CARD',
    LEAGUE_CREDIT: 'LEAGUE_CREDIT',
    CGA: 'CGA',
    HRA: 'HRA',
  });

  const getLinkDescriptionText = () => {
    if (isLifemarketTransaction) {
      return formatMessage({ id: 'GO_TO_LIFEMARKET' }, { invoiceId });
    }
    if (claimId) {
      return formatMessage({ id: 'GO_TO_CLAIM' }, { title });
    }
    if (paymentId) {
      return formatMessage({ id: 'GO_TO_RECEIPT' }, { title });
    }
    return '';
  };

  const isCredit = amountType === 'credit';
  const isLink = isLifemarketTransaction || claimId || paymentId;

  const handleLinkCallback = useConfigProperty(
    'core.routing.handleExternalLink',
    defaultHandleLink,
  );

  const handleLinkClick = () => {
    if (isLifemarketTransaction) {
      handleLinkCallback(`${lifemarketUrl}account/orders`);
    } else if (claimId) {
      navigate(`/member/claims/${claimId}?source=wallet`);
    } else if (paymentId) {
      navigate(`/member/receipt/${paymentId}`);
    }
  };

  return (
    <Box as="tr">
      <TableCell minWidth={100}>
        {formatDate(date, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </TableCell>
      <StyledTableCell maxWidth={120} title={referenceId || invoiceId}>
        {referenceId || invoiceId}
      </StyledTableCell>
      <TableCell fontWeight="bold">{title}</TableCell>
      <TableCell>
        {typeMessage(type as any)}
        {accountsList && (
          <BodyTwo>
            (
            {accountsList
              .map((account) => accountMessageMap(account as any))
              .join(', ')}
            )
          </BodyTwo>
        )}
      </TableCell>
      <TableCell
        minWidth={100}
        textAlign="end"
        fontWeight="bold"
        color={
          isCredit ? 'decorative.brand.primary.dark' : 'onSurface.text.primary'
        }
      >
        {amount && (
          <>
            {formatNumber(isCredit ? amount : -amount, {
              style: 'currency',
              currencyDisplay: 'symbol',
              currency,
              minimumFractionDigits: 2,
            })}
          </>
        )}
      </TableCell>
      <TableCell minWidth={30}>
        {isLink && (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: use button instead?
          <Link
            href="#"
            display="block"
            textAlign="end"
            onClick={handleLinkClick}
          >
            <VisuallyHidden>{getLinkDescriptionText()}</VisuallyHidden>
            <Icon icon="interfaceChevronForward" size="$one" />
          </Link>
        )}
      </TableCell>
    </Box>
  );
};
